<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-xl">
        <b-overlay :show="isExporting" no-center blur="">
          <template #overlay>
            <div class="position-absolute text-center"
              style="top: 350px; left: 50%; transform: translateX(-50%) translateY(-50%);">
              <ve-progress
                :progress="downloadProgress.percentCompleted"
                :loading="!downloadProgress.percentCompleted"
                :determinate="!downloadProgress.totalFileSize"
                :legend="downloadProgress.processedFileSize"
                :legend-formatter="({value}) => $n(downloadProgress.processedFileSize / 1024 / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1 })"
                animation="default 0 0"
                fontSize="1.25rem"
                :emptyColor="downloadProgress.emptyColor"
                :color="downloadProgress.color">
                <span slot="legend">
                   MB <span v-if="downloadProgress.totalFileSize">/ {{ $n(downloadProgress.totalFileSize / 1024 / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }} MB</span>
                </span>
                <span slot="legend-caption">
                  {{ downloadProgress.action }}
                </span>
              </ve-progress>
              <h3 class="mt-3">{{ $t('Webportal.Progress.Description') }}</h3>
            </div>
          </template>

          <b-card id="ReportFileExplorerCard" footer-class="overflow-auto">
            <template #header>
              <b-row class="form-row align-items-center no-wrap g-2">
                <div class="col-md-12">
                  <client-filter  v-model="clientFilter"
                                  :disabled="isExporting"
                                  :multipleClientSelection="false"
                                  @update:modelValue="loadReportFileData($event)"
                                  :auto-select="true" />
                </div>
              </b-row>

              <b-row class="form-row align-items-center no-wrap g-2 reportfile-toolbar">
                <div class="col d-flex justify-content-end align-items-center gap-2">
                  <b-form-group class="mb-0">
                    <pagination ref="pagination"
                                v-model="selectedPage"
                                :totalCount="totalCount"
                                :selectedPageSize="selectedPageSize"
                                @update:selectedPageSize="selectedPageSize = $event"
                                :disabled="isExporting"
                                :showAllRowsToolTip="$t('Webportal.ShowAllChecks')"
                                @input="selectedPage = $event; getReportFiles()" />
                  </b-form-group>
                  <b-form-group class="mb-0"
                      v-b-tooltip.hover.nofade="toolTipDelay"
                      :title="$t('Webportal.DownloadReportFiles')">
                    <b-button variant="primary"
                              :disabled="isExporting || downloadReportFilesDisabled"
                              @click="downloadReportFiles()">
                      <font-awesome-icon icon="file-download" />
                    </b-button>
                  </b-form-group>
                  <b-form-group class="mb-0" v-if="userHasAddReportFilesPermission" >
                    <router-link tag="button" type="button" class="btn btn-primary" to="/reportfileimport">
                      <font-awesome-icon :icon="['far', 'plus']" class="me-2" /> {{ $t('Webportal.AddReportFiles') }}
                    </router-link>
                  </b-form-group>
                </div>
              </b-row>
            </template>

            <div class="h-100" :class="{ spinner: isBusy }">
              <b-table ref="ReportFilesTable"
                      small hover responsive
                      :items="reportFileItems"
                      :fields="fields"
                      sticky-header="100%"
                      no-border-collapse
                      selectable
                      selected-variant="primary"
                      no-local-sorting
                      @row-selected="onRowSelected"
                      @sort-changed="sortChanged">

                <template #head(selected)>
                  <b-form-checkbox
                    :checked="allRowsSelected"
                    :indeterminate="selectedReportFiles && selectedReportFiles.length > 0 && !allRowsSelected"
                    @change="allRowsSelected ? $refs.ReportFilesTable?.clearSelected() : $refs.ReportFilesTable.selectAllRows()" />
                </template>

                <template #top-row>
                  <td></td>
                  <td v-if="userHasAddReportFilesPermission"></td>
                  <td>
                    <search-select  :value="columnFilter.ReportFileCategory.Name"
                                    @input="columnFilter.ReportFileCategory.Name = $event"
                                    @change="loadReportFileData"
                                    :options="reportFileCategoryItems"
                                    text-field="Name" value-field="Name"
                                    small :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                  </td>
                  <td v-if="userHasAddReportFilesPermission"></td>
                  <td v-if="userHasAddReportFilesPermission"></td>
                  <td></td>
                  <td>
                    <search-input :value="columnFilter.ReportFile.DisplayName"
                                  @input="columnFilter.ReportFile.DisplayName = $event"
                                  @change="loadReportFileData"
                                  small :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td></td>
                  <td>
                    <search-input :model="columnFilter.ReportFile.CreatedAt"
                                  @input="columnFilter.ReportFile.CreatedAt = $event"
                                  @change="loadReportFileData"
                                  small :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td></td>
                </template>

                <template #cell(selected)="data">
                  <b-form-checkbox
                    v-model="data.rowSelected"
                    @input="data.rowSelected ? $refs.ReportFilesTable.selectRow(data.index) : $refs.ReportFilesTable.unselectRow(data.index)" />
                </template>
                <template #[`cell(ReportFile.IsVisible)`]="data">
                  <div class="text-nowrap">
                    <font-awesome-icon :icon="['far', data.value ? 'eye' : 'eye-slash']" />
                    <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReportFile.EditModeRestricted.Description')">
                      <font-awesome-icon v-if="data.item.ReportFile.IsEditRestricted" :icon="['fas', 'user-lock']" class="text-danger" />
                    </span>
                  </div>
                </template>
                <template #cell(Properties)="data">
                  <div v-if="data.value" class="text-truncate" style="max-width:300px">
                    {{ getPropertiesString(data.value) }}
                  </div>
                </template>
                <template #cell(ClientLocations)="data">
                  <div v-if="data.value" class="text-truncate" style="max-width:300px">
                    {{ getLocationsString(data.value) }}
                  </div>
                </template>
                <template #[`cell(ReportFile.FileName)`]="data">
                  <font-awesome-icon :icon="getFileIcon(data.value)" size="lg" />
                </template>
                <template #[`cell(ReportFile.FileSize)`]="data">
                  <template v-if="data.value >= 0">
                    {{ $n(data.value / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }} KB
                  </template>
                </template>
                <template #[`cell(ReportFile.CreatedAt)`]="data">
                  {{ msm_date(data.value) }}
                </template>
                <template #cell(ActionDownload)="data">
                  <b-button variant="primary" size="sm"
                            v-b-tooltip.hover.nofade="toolTipDelay"
                            :title="$t('Webportal.Download')"
                            @click.stop="downloadReportFile(data.item.ReportFile.ReportFileId)">
                    <font-awesome-icon icon="file-download" fixed-width />
                  </b-button>
                </template>
              </b-table>
            </div>

            <template v-if="userHasAddReportFilesPermission" #footer>
              <div class="row">
                <div class="col position-static">
                  <report-file-properties v-model="reportFileProperties"
                                          :client-id="clientId"
                                          :report-files="selectedReportFiles"
                                          :report-file-categories="reportFileCategoryItems"
                                          :lock-properties="lockProperties"
                                          :disabled="isBusy" />
                </div>
                <div class="col-auto border-left">
                  <b-button-group vertical style="min-width:200px">
                    <b-button variant="primary" size="sm" class="mb-2"
                      :pressed.sync="lockProperties"
                      v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReportFile.LockValues.Description')">
                      <template v-if="lockProperties">
                        <font-awesome-icon :icon="['fas', 'lock']" class="me-2" /> {{ $t("Webportal.ReportFile.LockValues") }}
                      </template>
                      <template v-else>
                        <font-awesome-icon :icon="['fas', 'lock-open']" class="me-2" />  {{ $t("Webportal.ReportFile.MergeValues") }}
                      </template>
                    </b-button>
                    <b-button-group v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReportFile.ApplyToSelected.Description')">
                      <b-button variant="primary" size="sm" class="border-bottom"
                        :disabled="!isReportFileSelectionEditable"
                        @click="updateSelectedReportFiles()">
                        <font-awesome-icon :icon="['far', 'share-all']" class="me-2" /> {{ $t("Webportal.ReportFile.ApplyToSelected") }}
                      </b-button>
                    </b-button-group>
                    <b-button-group v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReportFile.SetVisible.Description')">
                      <b-button variant="primary" size="sm" class="border-top border-bottom"
                        :disabled="!isReportFileSelectionEditable || isReportFileSelectionEditRestricted"
                        @click="updateReportFileVisibility(true)">
                        <font-awesome-icon :icon="['far', 'eye']" class="me-2" /> {{ $t("Webportal.ReportFile.SetVisible") }}
                      </b-button>
                    </b-button-group>
                    <b-button-group v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReportFile.SetInvisible.Description')">
                      <b-button variant="primary" size="sm" class="border-top border-bottom"
                        :disabled="!isReportFileSelectionEditable || isReportFileSelectionEditRestricted"
                        @click="updateReportFileVisibility(false)">
                        <font-awesome-icon :icon="['far', 'eye-slash']" class="me-2" /> {{ $t("Webportal.ReportFile.SetInvisible") }}
                      </b-button>
                    </b-button-group>
                    <b-button-group class="mt-2" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReportFile.DeleteSelected.Description')">
                      <b-button variant="danger" size="sm"
                        :disabled="!isReportFileSelectionEditable || isReportFileSelectionEditRestricted"
                        @click="deleteSelectedReportFiles()">
                        <font-awesome-icon :icon="['far', 'trash-alt']" class="me-2" /> {{ $t("Webportal.ReportFile.DeleteSelected") }}
                      </b-button>
                    </b-button-group>
                  </b-button-group>
                </div>
              </div>
            </template>
          </b-card>
        </b-overlay>

        <!-- Modal: Confirm Delete Report Files -->
        <b-modal
          ref="modalConfirmDeleteReportFiles"
          :title="$t('Webportal.Dialog.ConfirmDelete.Title')"
          :ok-title="$t('Webportal.Dialog.Ok')"
          :cancel-title="$t('Webportal.Dialog.Cancel')"
          @ok="confirmDeleteReportFiles"
        >
          {{ $t('Webportal.Dialog.ConfirmDelete.Text') }}
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import DownloadFileMixin from '@/mixins/DownloadFileMixin';
import ReportFileMixin from '@/mixins/ReportFileMixin';

import ClientFilter from '@/components/ClientFilter-APP_TARGET';
import SearchInput from '@/components/SearchInput'
import SearchSelect from '@/components/SearchSelect'
import Pagination from '@/components/Pagination'
import ReportFileProperties from "./ReportFileProperties";

import EventBus from '@/services/EventBus.js';
import ReportFileService from '@/services/ReportFileService';

import CustomFilter from '@/plugins/CustomFilter';
import { useToast } from 'bootstrap-vue-next';

export default {
  name: 'ReportFileExplorer',

  components: { ClientFilter, SearchInput, SearchSelect, Pagination, ReportFileProperties },

  mixins: [ AppConfig, DownloadFileMixin, ReportFileMixin ],

  setup() {
    const { show } = useToast();
    return { showToast: show };
  },

  data() {
    return {
      clientFilter: {},

      isExporting: false,
      isBusy: false,                     // Determines if data loading is currently in progress

      columnFilter: {                       // Filter values for each column
        ReportFile:{
          CreatedAt: null,
          FileName: null,
          DisplayName: null },
        ReportFileCategory: { Name: null },
      },

      sortBy: null,                         // Sorting column
      sortDescending: false,                // Sorting order

      currentFilter: null,                  // Current filter object for change detection

      // Paging properties
      selectedPage: 1,                      // Number of the page, that is currently visible
      selectedPageSize: null,               // Number of items on one page of the table
      totalCount: 0,                        // Number of total items

      // Table properties
      reportFileCategoryItems: [],
      reportFileCancelToken: null,
      reportFileItems: [],
      selectedReportFiles: [],

      lockProperties: false,

      reportFileProperties: {},

      downloadProgress: null,
    }
  },

  computed: {
    filter: function() {
      // Creates a new object without empty values from source
      const isNotEmptyPredicate = o => !_.isEmpty(o);
      const pickByDeep = (obj, fn) =>
        _.pickBy( // Remove on root level
          _.mapValues(obj, (val, key) =>
            !_.isObjectLike(val) ? val : // Take plain values
              !_.isEmpty(_.pickBy(val, fn)) ? _.pickBy(val, fn) : null) // Map values of nested object
          , fn)

      return {
        clientIds: this.clientFilter.clientIds,
        propertyIds: this.clientFilter.propertyIds,
        columnFilter: pickByDeep(this.columnFilter, isNotEmptyPredicate),
        sortBy: this.sortBy,
        sortDescending: this.sortDescending,
      };
    },

    options() {
      return {
        page: this.selectedPage,
        pageSize: this.selectedPageSize
      }
    },

    fields() {
      return [
        { key: 'selected', thStyle: 'width:2em' },
        ... this.userHasAddReportFilesPermission ? [{ key: 'ReportFile.IsVisible', label: '', thStyle: 'width:2em' }] : [],
        { key: 'ReportFileCategory.Name', label: this.$t('Webportal.Category'), class: 'text-nowrap', thStyle: 'width:10em', sortable: true },
        ... this.userHasAddReportFilesPermission ? [{ key: 'Properties', label: this.$t('Webportal.Properties'), thStyle: 'width:1em', sortable: false }] : [],
        ... this.userHasAddReportFilesPermission ? [{ key: 'ClientLocations', label: this.$t('Webportal.Locations'), thStyle: 'width:1em', sortable: false }] : [],
        { key: 'ReportFile.FileName', label: '', class: 'text-center', thStyle: 'width:3em', sortable: false },
        { key: 'ReportFile.DisplayName', label: this.$t('Webportal.Filename'), thStyle: '', sortable: true },
        { key: 'ReportFile.FileSize', label: this.$t('Webportal.FileSize'), class: 'text-end text-nowrap', thStyle: 'width:7em', sortable: false },
        { key: 'ReportFile.CreatedAt', label: this.$t('Webportal.Created'), class: 'text-center', thStyle: 'width:7em', sortable: true },
        { key: 'ActionDownload', label: '', thStyle: 'width:3em' }
      ];
    },

    clientId() {
      return this.clientFilter?.clientIds?.length === 1 ? this.clientFilter.clientIds[0] : null;
    },

    allRowsSelected() {
      return this.selectedReportFiles?.length === this.reportFileItems?.length;
    },

    downloadReportFilesDisabled: function () {
      return _.isEmpty(this.reportFileItems) || _.isEmpty(this.selectedReportFiles);
    },

    selectedReportFileIds: function () {
      return this.selectedReportFiles.map(rf => rf.ReportFile.ReportFileId);
    },

    isReportFileSelectionEditable() {
      return this.selectedReportFiles.length > 0 &&
        this.reportFileProperties.reportFileCategory &&
        !this.isBusy;
    },

    isReportFileSelectionEditRestricted() {
      return this.selectedReportFiles.length > 0 &&
        this.selectedReportFiles.some(rf => rf.ReportFile.IsEditRestricted);
    },
  },

  mounted() {
    this.initializeTheme();
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeUnmount() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    msm_date(val) {
      return CustomFilter.msm_date(val)
    },

    localizationChanged(localizationId) {
      this.getReportFiles();
    },

    getPropertiesString(properties) {
      return properties ? properties.map(p => p.PropertyValueIds.map(pv => `${pv.split(/\^/s)[1]} (${p.PropertyName})`)).flat().join(', ') : '';
    },

    getLocationsString(clientLocations) {
      return clientLocations ? clientLocations.map(cl => [cl.ClientLocationIdOnClientSite ?? '', cl.Name ?? ''].join(' ')).join(', ') : '';
    },

    async getReportFiles() {
      if (!this.clientId)
        return;

      this.isBusy = true;

      // Cancel previous request
      if (this.reportFileCancelToken)
        this.reportFileCancelToken.cancel();
      this.reportFileCancelToken = axios.CancelToken.source();

      try {
        const response = await ReportFileService.getReportFiles(this.filter, this.options, this.reportFileCancelToken);
        this.$refs?.ReportFilesTable?.clearSelected();

        this.totalCount = response.data.TotalCount;
        this.reportFileItems = response.data.items;
        this.reportFileCategoryItems = response.data.ReportFileCategories
        this.isBusy = false;
      } catch(error) {
        if (!axios.isCancel(error))
        {
          this.isBusy = false;
          throw error;
        }
      }
    },

    loadReportFileData(ev) {
      if (!_.isEqual(this.filter, this.currentFilter)) {
        if (this.filter.clientIds !== this.currentFilter?.clientIds)
          this.columnFilter.ReportFileCategory.Name = null;

        this.currentFilter = this.filter;

        this.$refs.pagination.resetPaging();
        this.getReportFiles();
      }
    },

    onRowSelected(items) {
      this.selectedReportFiles = Array.isArray(items) ? items : [items];
    },

    sortChanged(e) {
      this.sortBy = e.sortBy;
      this.sortDescending = e.sortDesc;
      this.loadReportFileData();
    },

    async updateSelectedReportFiles() {
      this.isBusy = true;

      try {
        await ReportFileService.updateReportFiles(this.selectedReportFiles.map(rf => ({
          ReportFileId: rf.ReportFile.ReportFileId,
          ReportFileCategoryName: this.reportFileProperties.reportFileCategory,
          ClientId: rf.ReportFile.ClientId,
          PropertyValueIds: this.reportFileProperties.propertyIds ? this.reportFileProperties.propertyIds.join("|") : "",
          ClientLocationIds: this.reportFileProperties.clientLocationIds ?? []
        })));

        await this.getReportFiles();
      } catch (error) {
        this.showToast({
          title: this.$t("Webportal.Error"),
          message: error.response?.data?.message || error.message,
          props: {
            autoHide: false,
            variant: "danger",
            noCloseButton: false,
          }
        });
      } finally {
        this.isBusy = false;
      }
    },

    async updateReportFileVisibility(isVisible) {
      this.isBusy = true;

      try {
        await ReportFileService.updateReportFiles(this.selectedReportFiles.map(rf => ({
          ReportFileId: rf.ReportFile.ReportFileId,
          IsVisible: isVisible
        }))).catch(error => {
          if (!axios.isCancel(error)) {
            console.error("Error updating report file visibility: ", error);
          }
        });

        await this.getReportFiles();
      } finally {
        this.isBusy = false;
      }
    },

    async deleteSelectedReportFiles() {
      // Zeige Confirm-Modal
      this.$refs.modalConfirmDeleteReportFiles.show();
    },

    async confirmDeleteReportFiles() {
      // Wird aufgerufen, wenn der User OK klickt
      this.isBusy = true;

      try {
        await ReportFileService.deleteReportFiles(this.selectedReportFileIds);
        await this.getReportFiles();
      } finally {
        this.isBusy = false;
      }
    },

    async downloadReportFile(id) {
      this.isBusy = true;

      try {
        const response = await ReportFileService.downloadReportFile(id);
        this.downloadFile(response);
      } finally {
        this.isBusy = false;
      }
    },

    async downloadReportFiles() {
      this.isExporting = true;
      this.downloadProgress = {
        action: this.$t('Webportal.Progress.Initiating').toUpperCase(),
        percentCompleted: 0,
        processedFileSize: null,
        totalFileSize: undefined,
        emptyColor: getComputedStyle(document.body).getPropertyValue('--spinner-bg') || undefined,
        color: getComputedStyle(document.body).getPropertyValue('--spinner-color') || undefined
      };

      try {
        const that = this;

        const response = await ReportFileService.downloadReportFiles(
          this.selectedReportFileIds,
          // IMPORTANT: We're using standard (no arrow) function so that "this" points to the actual XMLHttpRequest for accessing the response headers
          function(progressEvent) {
            if (that.downloadProgress.totalFileSize === undefined)
            {
              that.downloadProgress.action = that.$t('Webportal.Progress.Downloaded').toUpperCase();
              that.downloadProgress.totalFileSize = this.getResponseHeader('total-file-size');
            }

            that.downloadProgress.processedFileSize = progressEvent.loaded;
            if (that.downloadProgress.totalFileSize > 0)
              that.downloadProgress.percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / that.downloadProgress.totalFileSize), 100);
            else
              that.downloadProgress.percentCompleted = 0.1; // ensure visibility of processedFileSize (legend-value)
          });

          if (response.status === 204) {
            this.showToast({
              title: this.$t("Webportal.NoDataFound"),
              props: {
                autoHide: true,
                delay: 5000,
                variant: "warning",
                noCloseButton: true,
              }
            });
          } else {
            this.downloadFile(response);
          }

      } finally {
        this.isExporting = false;
      }
    },

  }
}
</script>

<style lang="scss">
.reportfile-toolbar {
  .page-link,
  .btn {
    height: 2.5rem;
    display: flex;
    align-items: center;
  }
}
</style>
